import React, { useEffect, useState } from 'react';
import { CalendarClock, Plus, Trash2, Pencil, Loader2, History } from 'lucide-react';
import { toast } from 'sonner';
import { JOB_STATUS_META, GANTT_ROWS } from '../mock/data';
import { PageHeader, StatusBadge, Modal, Field, HistoryModal } from '../components/common';
import { useRealtime } from '../realtime/RealtimeContext';
import { formatShift } from '../utils/shifts';
import api from '../api/client';

const HOURS = ['12:00', '14:00', '16:00', '18:00', '20:00', '22:00', '0:00', '2:00', '4:00', '6:00', '8:00', '10:00', '12:00', '14:00'];
const STATUSES = ['PLANNED', 'IN_PROGRESS', 'COMPLETED', 'ON_HOLD'];
const EMPTY = { code: '', product: '', machine: 'M02', qty: '0/100', start: '', end: '', status: 'PLANNED', shift: '', material: '' };

export default function ProductionScheduling() {
  const [range, setRange] = useState('24h');
  const [jobs, setJobs] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);

  const shiftOptions = shifts.map(formatShift);

  const load = () => api.listJobs().then(setJobs).catch(() => {}).finally(() => setLoading(false));
  const loadShifts = () => api.getShifts().then(setShifts).catch(() => {});
  const loadMaterials = () => api.listMaterials().then(setMaterials).catch(() => {});
  useEffect(() => { load(); loadShifts(); loadMaterials(); }, []);
  useRealtime(() => { load(); loadShifts(); loadMaterials(); });

  const openCreate = () => { setForm(EMPTY); setModal({ mode: 'create' }); };
  const openEdit = (j) => { setForm({ ...j }); setModal({ mode: 'edit' }); };

  const save = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'create') {
        const created = await api.createJob(form);
        setJobs((p) => [...p, created]);
        toast.success(`${created.code} added`);
      } else {
        const upd = await api.updateJob(form.code, form);
        setJobs((p) => p.map((x) => (x.code === upd.code ? upd : x)));
        toast.success(`${upd.code} updated`);
      }
      setModal(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const changeStatus = async (job, status) => {
    setJobs((p) => p.map((x) => (x.code === job.code ? { ...x, status } : x)));
    try { await api.updateJob(job.code, { status }); toast.success(`${job.code} → ${status.replace('_', ' ')}`); }
    catch (e) { toast.error('Update failed'); load(); }
  };

  const remove = async (code) => {
    setJobs((p) => p.filter((x) => x.code !== code));
    try { await api.deleteJob(code); toast.success(`${code} removed`); }
    catch (e) { toast.error('Delete failed'); load(); }
  };

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={CalendarClock} title="Production Scheduling (APS)" subtitle={`${jobs.length} jobs · Gantt view`}
        right={<>
          <select className="kmt-input w-24" value={range} onChange={(e) => setRange(e.target.value)}>
            <option>8h</option><option>24h</option><option>48h</option><option>7d</option>
          </select>
          <button className="kmt-btn-gold flex items-center gap-2" onClick={openCreate}><Plus size={16} />New Job</button>
        </>} />

      <div className="p-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-24" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={28} /></div>
        ) : (
          <div className="kmt-panel overflow-x-auto">
            <table className="w-full kmt-table">
              <thead><tr>
                <th className="kmt-th">Job Code</th><th className="kmt-th">Product</th><th className="kmt-th">Machine</th><th className="kmt-th">Raw Material</th><th className="kmt-th">Qty</th><th className="kmt-th">Shift</th><th className="kmt-th">Planned Start</th><th className="kmt-th">Planned End</th><th className="kmt-th">Status</th><th className="kmt-th">Actions</th>
              </tr></thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.code}>
                    <td className="kmt-td font-mono-k" data-label="Job Code" style={{ color: 'var(--kmt-gold)' }}>{j.code}</td>
                    <td className="kmt-td" data-label="Product">{j.product}</td>
                    <td className="kmt-td font-mono-k" data-label="Machine">{j.machine}</td>
                    <td className="kmt-td" data-label="Raw Material" style={{ color: j.material ? 'var(--kmt-green)' : 'var(--kmt-muted)' }}>{j.material || '—'}</td>
                    <td className="kmt-td font-mono-k" data-label="Qty">{j.qty}</td>
                    <td className="kmt-td" data-label="Shift" style={{ color: 'var(--kmt-muted)' }}>{j.shift || '—'}</td>
                    <td className="kmt-td" data-label="Start" style={{ color: 'var(--kmt-muted)' }}>{j.start}</td>
                    <td className="kmt-td" data-label="End" style={{ color: 'var(--kmt-muted)' }}>{j.end}</td>
                    <td className="kmt-td" data-label="Status">
                      <select className="kmt-input" style={{ padding: '5px 8px', fontSize: 12, width: 140 }}
                        value={j.status} onChange={(e) => changeStatus(j, e.target.value)}>
                        {STATUSES.map((s) => <option key={s} value={s}>{(JOB_STATUS_META[s] || {}).label || s}</option>)}
                      </select>
                    </td>
                    <td className="kmt-td" data-label="Actions">
                      <div className="flex gap-2">
                        <button onClick={() => setHistory(j.code)} title="History" style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-blue)]"><History size={15} /></button>
                        <button onClick={() => openEdit(j)} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-gold)]"><Pencil size={15} /></button>
                        <button onClick={() => remove(j.code)} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-red)]"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Gantt (visual) */}
        <div className="kmt-panel p-5">
          <div className="kmt-label mb-4">Gantt Timeline · Aug 21 12:16 PM → Aug 22 6:16 PM</div>
          <div className="overflow-x-auto">
            <div style={{ minWidth: 900 }}>
              <div className="flex" style={{ paddingLeft: 130 }}>
                {HOURS.map((h, i) => <div key={i} className="flex-1 font-mono-k text-xs" style={{ color: 'var(--kmt-muted-2)' }}>{h}</div>)}
              </div>
              <div className="mt-2 space-y-2">
                {jobs.length === 0 && <div className="text-sm py-4" style={{ color: 'var(--kmt-muted)' }}>No jobs scheduled. Add a job above to see it on the timeline.</div>}
                {jobs.map((j, idx) => {
                  const color = { COMPLETED: 'var(--kmt-green)', IN_PROGRESS: 'var(--kmt-blue)', ON_HOLD: 'var(--kmt-amber)', PLANNED: 'var(--kmt-muted)' }[j.status] || 'var(--kmt-gold)';
                  const qm = /(\d+)\s*\/\s*(\d+)/.exec(j.qty || '');
                  const pct = qm && +qm[2] ? Math.min(100, Math.round((+qm[1] / +qm[2]) * 100)) : 0;
                  const span = 24 + (idx % 4) * 14; // staggered visual span
                  const offset = (idx * 11) % 55;
                  return (
                    <div key={j.code} className="flex items-center">
                      <div style={{ width: 130 }} className="shrink-0">
                        <div className="font-mono-k text-xs" style={{ color: 'var(--kmt-gold)' }}>{j.machine}</div>
                        <div className="text-xs truncate" style={{ color: 'var(--kmt-muted)' }}>{j.code}</div>
                      </div>
                      <div className="relative flex-1 h-9" style={{ background: '#0e0e10', borderRadius: 6 }}>
                        <div className="absolute top-1 bottom-1 rounded flex items-center px-2 overflow-hidden"
                          style={{ left: `${offset}%`, width: `${span}%`, background: 'rgba(255,255,255,0.04)', border: `1px solid ${color}` }}>
                          <div className="absolute left-0 top-0 bottom-0" style={{ width: `${pct}%`, background: color, opacity: 0.25 }} />
                          <span className="text-xs truncate relative" style={{ color }}>{j.product} · {pct}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)}
        title={modal?.mode === 'create' ? 'New Job' : `Edit ${form.code}`}
        footer={<>
          <button className="kmt-btn-ghost" onClick={() => setModal(null)}>Cancel</button>
          <button className="kmt-btn-gold" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
        </>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Job Code">
            <input className="kmt-input" value={form.code} disabled={modal?.mode === 'edit'} placeholder="auto-generated if empty" onChange={(e) => setForm({ ...form, code: e.target.value })} />
          </Field>
          <Field label="Machine">
            <input className="kmt-input" value={form.machine} onChange={(e) => setForm({ ...form, machine: e.target.value })} />
          </Field>
          <Field label="Raw Material">
            <select className="kmt-input" value={form.material || ''} onChange={(e) => setForm({ ...form, material: e.target.value })} data-testid="job-material-select">
              <option value="">— None —</option>
              {!materials.some((m) => m.name === form.material) && form.material && <option value={form.material}>{form.material}</option>}
              {materials.map((m) => <option key={m.code} value={m.name}>{m.name} ({m.code} · {m.stock} {m.unit})</option>)}
            </select>
          </Field>
          <div className="col-span-2">
            <Field label="Product">
              <input className="kmt-input" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} />
            </Field>
          </div>
          <Field label="Quantity (done/total)">
            <input className="kmt-input" value={form.qty} placeholder="0/500" onChange={(e) => setForm({ ...form, qty: e.target.value })} />
          </Field>
          <Field label="Status">
            <select className="kmt-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{(JOB_STATUS_META[s] || {}).label || s}</option>)}
            </select>
          </Field>
          <Field label="Shift">
            <select className="kmt-input" value={form.shift || ''} onChange={(e) => setForm({ ...form, shift: e.target.value })} data-testid="job-shift-select">
              <option value="">— None —</option>
              {!shiftOptions.includes(form.shift) && form.shift && <option value={form.shift}>{form.shift}</option>}
              {shiftOptions.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Planned Start">
            <input className="kmt-input" value={form.start} placeholder="8/22, 6:00 AM" onChange={(e) => setForm({ ...form, start: e.target.value })} />
          </Field>
          <Field label="Planned End">
            <input className="kmt-input" value={form.end} placeholder="8/22, 2:00 PM" onChange={(e) => setForm({ ...form, end: e.target.value })} />
          </Field>
        </div>
      </Modal>

      <HistoryModal open={!!history} onClose={() => setHistory(null)} entityType="job" entityId={history} api={api} />
    </div>
  );
}
